import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fetchAccountSummary, summarise, normaliseAccount, persistSummary } from "@/lib/derivWs";
import { toast } from "sonner";

export default function AuthCallback() {
  const nav = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // --- PATH A: OAuth 2.0 + PKCE flow (frontend-side, VTS-style) ---
    // Deriv redirects back with ?code=…&state=…. The verifier was stashed in sessionStorage
    // before we kicked the user off to Deriv (see connectToDeriv() in lib/api.js). We send
    // code + verifier + state to the backend which swaps them for a Deriv access_token.
    const code = params.get("code");
    const state = params.get("state");
    if (code && state) {
      const verifier = sessionStorage.getItem("naevo_pkce_verifier");
      const expectedState = sessionStorage.getItem("naevo_oauth_state");
      sessionStorage.removeItem("naevo_pkce_verifier");
      sessionStorage.removeItem("naevo_oauth_state");
      if (!verifier) {
        toast.error("OAuth session expired. Please click Connect again.");
        nav("/");
        return;
      }
      if (expectedState && expectedState !== state) {
        toast.error("OAuth state mismatch. Please click Connect again.");
        nav("/");
        return;
      }
      const redirect_uri = `${window.location.origin}/auth/callback`;
      (async () => {
        try {
          // 1. Swap code for an access_token on the backend (talks to auth.deriv.com/oauth2/token)
          const r = await api.post("/auth/callback-exchange", { code, state, code_verifier: verifier, redirect_uri });
          const token = r.data.token;
          if (!token) throw new Error("No access_token in exchange response");
          // 2. Accounts + balances: browser REST first, backend list as fallback
          let summary;
          try {
            summary = await fetchAccountSummary(token);
          } catch {
            summary = summarise((r.data.accounts || []).map((a) => normaliseAccount(a, token)));
          }
          localStorage.setItem("naevo_session", r.data.session_id);
          if (summary.loginid) {
            persistSummary(summary, token);
            api.post("/account/select", { session_id: r.data.session_id, account_id: summary.loginid }).catch(() => {});
            const ref = localStorage.getItem("naevo_ref");
            if (ref) api.post("/player/referral/claim", { session_id: r.data.session_id, code: ref }).then(() => localStorage.removeItem("naevo_ref")).catch(() => {});
          } else {
            localStorage.setItem("naevo_token", token);
            localStorage.setItem("naevo_loginid", "UNKNOWN");
          }
          const loginid = summary.loginid || "account";
          const isDemo = summary.is_virtual;
          window.dispatchEvent(new Event("naevo-session-change"));
          toast.success(`Connected ${loginid} · ${isDemo ? "Demo" : "Real"}`);
          nav("/dashboard");
        } catch (e) {
          toast.error(e.response?.data?.detail || e.message || "Deriv OAuth exchange failed");
          nav("/");
        }
      })();
      return;
    }

    // --- PATH B: Legacy Deriv flow (?acct1=&token1=&cur1=) — kept for legacy numeric app ids ---
    const accounts = [];
    for (let i = 1; i < 10; i++) {
      const acct = params.get(`acct${i}`);
      const token = params.get(`token${i}`);
      const cur = params.get(`cur${i}`);
      if (acct && token) accounts.push({ loginid: acct, token, currency: cur || "USD" });
    }
    if (accounts.length === 0) {
      // Deriv may redirect with ?error=... when it refuses
      const err = params.get("error_description") || params.get("error");
      toast.error(err || "No Deriv account linked. Please try Connect again.");
      nav("/");
      return;
    }
    api
      .post("/auth/session", { accounts })
      .then((r) => {
        localStorage.setItem("naevo_session", r.data.session_id);
        localStorage.setItem("naevo_loginid", r.data.loginid || "");
        localStorage.setItem("naevo_currency", r.data.currency || "USD");
        const isDemo = /^VR/.test(r.data.loginid || "");
        localStorage.setItem("naevo_account_type", isDemo ? "demo" : "real");
        toast.success(`Connected ${r.data.loginid} · ${isDemo ? "Demo" : "Real"}`);
        nav("/dashboard");
      })
      .catch(() => {
        toast.error("Failed to save session");
        nav("/");
      });
  }, [nav]);
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="font-display text-3xl">Connecting your Deriv account…</div>
        <div className="text-white/50 mt-2">Please wait</div>
      </div>
    </div>
  );
}
